/**
 * Per-game availability (GCC sorting redesign, Phase 4).
 *
 * The ONLY availability signal is the backend's `available: false`. The field is
 * absent until FEATURE_GAME_FEED_RESOLVER is switched on, so every helper here
 * fails OPEN: undefined / true / null / 0 / "false" all read as AVAILABLE. There
 * is no FE flag read and no env var.
 *
 * Pure module — no React, no side effects beyond the toggle's sessionStorage
 * write and its broadcast event.
 */

export const SHOW_UNAVAILABLE_KEY = "showUnavailableGames";
export const SHOW_UNAVAILABLE_EVENT = "gcc:show-unavailable-changed";

/** The ONLY availability signal. Fail-open: anything but the literal false is available. */
export function isGameAvailable(game) {
  return !game || game.available !== false ? true : false;
}

export function hasUnavailableGames(list) {
  return Array.isArray(list) && list.some((g) => g && g.available === false);
}

export function filterGamesForDisplay(list, showUnavailable) {
  if (!Array.isArray(list)) return list;
  if (showUnavailable) return list;
  return list.filter(isGameAvailable);
}

/**
 * Slider source: FILTER FIRST, then pad by repetition to fill a row.
 * Every guard and modulo must run against the FILTERED array — padding from an
 * emptied array would push `undefined` into the slider and crash GameItem's
 * game.id deref.
 */
export function buildSliderGames(list, minSlides, showUnavailable) {
  const source = filterGamesForDisplay(Array.isArray(list) ? list : [], showUnavailable) || [];
  const need = Math.max(Number(minSlides) || 0, 1);
  if (!source.length) return [];
  if (source.length >= need) return source;
  const padded = [...source];
  let i = 0;
  while (padded.length < need) {
    padded.push(source[i % source.length]);
    i += 1;
  }
  return padded;
}

// Blocked sessionStorage (Safari private mode / strict ITP) must NOT turn the
// switch into a dead one. Every subscriber handler re-reads getShowUnavailable(),
// so if that only ever consulted a storage that refuses writes, flipping the
// toggle would broadcast and change nothing. The module therefore keeps the value
// in memory too and serves it whenever storage is unusable: the toggle genuinely
// works for the mounted tab session, it just does not survive a reload.
let inMemoryShowUnavailable = true; // default ON
let storageBlocked = false;

export function getShowUnavailable() {
  if (storageBlocked) return inMemoryShowUnavailable;
  try {
    const raw = window.sessionStorage.getItem(SHOW_UNAVAILABLE_KEY);
    return raw === null ? true : raw === "true"; // default ON
  } catch (e) {
    storageBlocked = true;
    return inMemoryShowUnavailable;
  }
}

export function setShowUnavailable(value) {
  const next = !!value;
  // Written unconditionally and BEFORE the storage attempt, so the broadcast
  // below always finds the new value even when the write throws.
  inMemoryShowUnavailable = next;
  try {
    window.sessionStorage.setItem(SHOW_UNAVAILABLE_KEY, next ? "true" : "false");
  } catch (e) {
    storageBlocked = true; // fall back to the in-memory value from here on
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SHOW_UNAVAILABLE_EVENT));
  }
  return next;
}
